// Shared recon Dashboard shell for every PSP. Both Authorize.Net and PaySimple render THIS component,
// driven only by a per-PSP `config` (what to load, how to label the account, which tiles each section
// shows). Changing one PSP's dashboard cannot change the other's UI/UX — the shell is identical and
// only the config data differs.
//
// config = {
//   provider,                      // 'authnet' | 'paysimple' | 'stripe' — logo + nav + accent
//   title,                         // header title (usually 'Dashboard')
//   accounts,                      // bool: show the account dropdown + "All accounts" option
//   syncTitle,                     // tooltip for the Sync-now button
//   buildNav(active, account, navigate),   // section-nav tabs (dashboard active)
//   load(account) -> data,         // async; return any blob the tiles need (per-endpoint errors stashed inside)
//   lastSynced(data) -> string|null,       // text under Sync now
//   accountsList(data) -> [{key,name}],    // dropdown options (only read when config.accounts)
//   sections(ctx) -> [{ label, tiles:[{ label, value, sub, color, onClick }] }],
// }
// ctx = { data, account, navigate, loading }. A tile with no onClick is non-clickable; color is an MUI
// palette key ('primary'|'success'|'warning'|'error') or null for the neutral card.
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Typography, CircularProgress, Alert, Button } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '../common/PageLayout';
import WorkspaceHeader from './WorkspaceHeader';
import AutocreateLogDialog from './AutocreateLogDialog';

function StatCard({ label, value, sub, color, onClick }) {
  return (
    <Paper variant="outlined" onClick={onClick}
      sx={{ p: 2, height: '100%', borderTop: 3, borderTopColor: color ? `${color}.main` : 'divider',
        cursor: onClick ? 'pointer' : 'default', transition: '.12s', '&:hover': onClick ? { boxShadow: 3 } : {} }}>
      <Typography variant="caption" sx={{ color: color ? `${color}.dark` : 'text.secondary', fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: color ? `${color}.dark` : 'text.primary' }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
    </Paper>
  );
}

export default function ReconDashboard({ config }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const account = config.accounts ? (sp.get('acct') || 'all') : 'all';
  const setAccount = (a) => setSp((prev) => { const p = new URLSearchParams(prev); p.set('acct', a); return p; }, { replace: true });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await config.load(account)); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [config, account]);
  useEffect(() => { load(); }, [load]);

  const ctx = { data, account, navigate, loading };
  const sections = (!loading && data) ? config.sections(ctx) : [];

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <WorkspaceHeader
        provider={config.provider} title={config.title || 'Dashboard'}
        {...(config.accounts ? {
          account, accounts: config.accountsList ? (config.accountsList(data) || []) : [],
          onAccount: setAccount, accountAllOption: true,
        } : { accountAllOption: false })}
        onSync={load} syncing={loading} syncTitle={config.syncTitle}
        lastSynced={config.lastSynced ? config.lastSynced(data) : null}
        sectionNav={config.buildNav('dashboard', account, navigate)}
        actions={<Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => setLogOpen(true)}>Auto-create log</Button>}
      />
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : (
        <>
          {sections.map((section, si) => (
            <React.Fragment key={section.label || si}>
              <Typography variant="overline" sx={{ color: 'text.secondary', ...(si > 0 ? { mt: 1, display: 'block' } : {}) }}>{section.label}</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: si === sections.length - 1 ? 3 : 2, mt: 0.5 }}>
                {section.tiles.map((t, ti) => (
                  <Box key={t.label || ti} sx={{ flex: '1 1 150px' }}>
                    <StatCard {...t} />
                  </Box>
                ))}
              </Box>
              {section.note && <Alert severity="warning" sx={{ mb: 2 }}>{section.note}</Alert>}
            </React.Fragment>
          ))}
        </>
      )}
      <AutocreateLogDialog open={logOpen} onClose={() => setLogOpen(false)} />
    </PageLayout>
  );
}
